import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce";
import { CustomerGroup, customerGroupsApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { customerGroupsQueryKey } from "./customerGroupsQueryKey";

interface CustomerGroupSelectProps {
  value?: number | null;
  onChange: (groupId: number) => void;
  disabled?: boolean;
  id?: string;
  /** Com onClear, a lista ganha uma opção sem grupo (ex.: "Todos os grupos" no filtro, "Sem grupo" no cadastro). */
  onClear?: () => void;
  clearLabel?: string;
  ariaLabel?: string;
}

// Grupos por busca: a lista não é carregada inteira, então mostra os primeiros e pede para refinar.
const SEARCH_PAGE_SIZE = 50;

const groupLabel = (group: CustomerGroup) => (
  <>
    {group.name}
    {group.name !== group.code && <span className="text-muted-foreground"> ({group.code})</span>}
  </>
);

/**
 * Combobox dos grupos de cliente ativos, com busca por nome, código ou ID do Magento feita na API (paginada).
 * Compartilhado pelos cadastros PJ e PF.
 */
export function CustomerGroupSelect({
  value,
  onChange,
  disabled,
  id,
  onClear,
  clearLabel = "Sem grupo",
  ariaLabel = "Grupo do cliente",
}: CustomerGroupSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);

  const listQuery = useQuery({
    queryKey: [...customerGroupsQueryKey, "select", debouncedSearch],
    queryFn: () =>
      customerGroupsApi.list({ search: debouncedSearch, status: "active", pageSize: SEARCH_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  // O grupo escolhido pode não estar na página da busca: carrega pelo id para mostrar o nome.
  const selectedQuery = useQuery({
    queryKey: [...customerGroupsQueryKey, "byId", value],
    queryFn: () => customerGroupsApi.getById(value!),
    enabled: value != null,
  });

  const groups = listQuery.data?.data ?? [];
  const total = listQuery.data?.total ?? 0;
  const selected = selectedQuery.data ?? groups.find((group) => group.id === value);
  // Sem busca e sem resultado: não há grupo ativo cadastrado.
  const noGroups = !listQuery.isLoading && !debouncedSearch && total === 0;

  const placeholder = listQuery.isLoading
    ? "Carregando grupos..."
    : listQuery.isError
      ? "Não foi possível carregar os grupos"
      : noGroups
        ? "Nenhum grupo importado"
        : onClear
          ? clearLabel
          : "Selecione o grupo";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label={ariaLabel}
          disabled={disabled || listQuery.isLoading || listQuery.isError || noGroups}
          className="w-full justify-between font-normal"
        >
          {selected ? (
            <span className="truncate">{groupLabel(selected)}</span>
          ) : (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-64 p-0" align="start">
        {/* A API já filtra; o cmdk não filtra de novo. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nome, código ou ID do Magento..."
            aria-label="Buscar grupo do cliente"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{listQuery.isFetching ? "Buscando..." : "Nenhum grupo encontrado."}</CommandEmpty>
            <CommandGroup>
              {onClear && !debouncedSearch && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onClear();
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4 shrink-0", value == null ? "opacity-100" : "opacity-0")}
                    aria-hidden="true"
                  />
                  <span className="truncate">{clearLabel}</span>
                </CommandItem>
              )}
              {groups.map((group) => (
                <CommandItem
                  key={group.id}
                  value={String(group.id)}
                  onSelect={() => {
                    onChange(group.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn("mr-2 h-4 w-4 shrink-0", group.id === value ? "opacity-100" : "opacity-0")}
                    aria-hidden="true"
                  />
                  <span className="truncate">{groupLabel(group)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {total > groups.length && (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                Mostrando {groups.length} de {total}. Digite para refinar a busca.
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
